/**
 * Centralized brand list.
 *
 * - `BRANDS_FALLBACK`: static fallback used until the backend response arrives
 *   (or if the API call fails).
 * - `useBrands()`: React hook that returns the current brand list. Fetches
 *   `GET /api/brands` once per session and caches the result in module-level
 *   state so every consumer shares the same list.
 *
 * Keep `BRANDS_FALLBACK` in sync with backend `AVAILABLE_BRANDS` in
 * /app/backend/server.py — it is the offline default when the network is slow
 * or unavailable.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

export const BRANDS_FALLBACK = [
  'MH Cockpit',
  'MH Intellect',
  'MH Cognition',
  'MHC Global',
  'MH AgroTech',
  'MH Connekt',
  'MH Lead',
  'Vels',
  'Crescent',
  'Bharath',
  'Alliance',
  'Bishop Heber',
  'Jamal',
  'Kalasalingam',
  'Sanskriti',
  'Sasuri',
  'Other',
];

// Module-level cache shared across all hook instances.
let cachedBrands = null;
let inFlight = null;
const listeners = new Set();

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fetchBrands = (token) => {
  if (!token) return Promise.resolve(BRANDS_FALLBACK);
  if (!inFlight) {
    inFlight = axios
      .get(`${API}/brands`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const list = Array.isArray(res?.data?.brands) ? res.data.brands : null;
        cachedBrands = list && list.length ? list : BRANDS_FALLBACK;
        return cachedBrands;
      })
      .catch(() => {
        cachedBrands = BRANDS_FALLBACK;
        return cachedBrands;
      })
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
};

/** Force-refresh the cached brand list. Components subscribed via useBrands() re-render. */
export const refreshBrands = async (token) => {
  cachedBrands = null;
  const list = await fetchBrands(token);
  listeners.forEach((cb) => cb(list));
  return list;
};

export const useBrands = () => {
  const { token } = useAuth();
  const [brands, setBrands] = useState(cachedBrands || BRANDS_FALLBACK);

  useEffect(() => {
    listeners.add(setBrands);
    return () => {
      listeners.delete(setBrands);
    };
    // `listeners` is a module-level singleton Set, intentionally excluded.
  }, []);

  useEffect(() => {
    if (cachedBrands || !token) return;
    let mounted = true;
    fetchBrands(token).then((list) => {
      if (mounted) setBrands(list);
    });
    return () => {
      mounted = false;
    };
    // `cachedBrands`/`fetchBrands` are module-level singletons, intentionally
    // excluded so this effect only re-runs on `token`.
  }, [token]);

  return brands;
};

export default useBrands;
